import { useNavigate } from 'react-router-dom';
import { UserPlus, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Card the assistant renders for a `[PROPOSE: patient(...)]` marker — the
 * doctor asked MAIE to register a new patient. Unlike the order cards
 * (which auto-place + Undo), creating a patient is deliberately MANUAL and
 * never happens server-side from chat: this card just shows the details
 * MAIE parsed and hands them to the real New Patient form (prefilled) where
 * the doctor reviews, completes, and creates — proper validation, dedup,
 * and tenant scope. So the assistant must never claim the patient "exists".
 */
interface PatientProposalCardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: any; // { type:'propose_patient', parameters:{ items:[{...}] } }
  onDismiss: () => void;
}

const isGender = (g: unknown): g is 'male' | 'female' | 'other' =>
  g === 'male' || g === 'female' || g === 'other';

export function PatientProposalCard({
  action,
  onDismiss,
}: PatientProposalCardProps) {
  const navigate = useNavigate();
  const item = (action?.parameters?.items?.[0] ?? {}) as Record<string, unknown>;

  const firstName = String(item.firstName ?? '').trim();
  const lastName = String(item.lastName ?? '').trim();
  const fullName =
    [firstName, lastName].filter(Boolean).join(' ') || 'New patient';

  const dob = String(item.dateOfBirth ?? '').trim();
  const ageYears =
    typeof item.ageYears === 'number' && item.ageYears > 0
      ? item.ageYears
      : undefined;
  const gender = isGender(item.gender) ? item.gender : undefined;
  const phone = String(item.phone ?? '').trim();
  const email = String(item.email ?? '').trim();

  // Compact detail chips — only what MAIE actually extracted.
  const chips: string[] = [];
  if (dob) chips.push(dob);
  else if (ageYears) chips.push(`${ageYears} yo`);
  if (gender) chips.push(gender);
  if (phone) chips.push(phone);
  if (email) chips.push(email);

  const handleReview = () => {
    // Hand off to AddPatientPage's existing `extractedInfo` prefill contract
    // (recordingSessionApi.ExtractedPatientInfo). Omit fields MAIE didn't get.
    const extractedInfo = {
      ...(firstName ? { firstName } : {}),
      ...(lastName ? { lastName } : {}),
      ...(dob ? { dateOfBirth: dob } : {}),
      ...(ageYears ? { ageYears } : {}),
      ...(gender ? { gender } : {}),
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
      ...(item.address ? { address: String(item.address) } : {}),
    };
    navigate('/patients/new', { state: { extractedInfo } });
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-start gap-2">
        <div
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{
            background: 'var(--color-primary-500)',
            color: 'var(--color-text-inverse, #fff)',
          }}
        >
          <UserPlus className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-[13px] font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {fullName}
          </div>
          {chips.length > 0 && (
            <div
              className="mt-0.5 text-[12px]"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {chips.join(' · ')}
            </div>
          )}
          <div
            className="mt-1 text-[11.5px]"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            Review the details and create — nothing is saved until you do.
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="rounded-md p-1"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleReview} className="gap-1">
          Review &amp; create
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}
